import { ViteSSG } from 'vite-ssg';
import { ID_INJECTION_KEY } from 'element-plus/es';
import 'element-plus/dist/index.css';
import 'virtual:svg-icons-register';
import components from './components';
import VueAnimateOnScroll from '@/directives/scroll-animate';
import VueViewPort from '@/directives/view-port';
import confirm from '@/components/Modal';

import {
  fpjsPlugin,
  FpjsVueOptions
} from '@fingerprintjs/fingerprintjs-pro-vue-v3';
import * as Sentry from '@sentry/vue';
import App from './App.vue';
import { useRouterEach, routes } from './router';
import { pinia } from './store';
import { i18n } from '@/lang/i18n';
// 自定义拖拽
import Directives from '@/utils/directives';
import { envHost } from '@/config/setup';
import { getTimeZone } from '@/utils/time';
import { useGlobalAttrs } from '@/config/attrs';

export const createApp = ViteSSG(
  App,
  // router 实例初始化设置
  {
    routes
  },
  (context: any) => {
    const { app, router, initialState, isClient, onSSRAppRendered } = context;
    const visitorHost = envHost.replace('www', 'visitor');

    app
      .use(i18n)
      .use(pinia)
      .use(Directives)
      .use(components)
      .use(fpjsPlugin, {
        loadOptions: {
          apiKey: '5OLLFTQ28cF3z3tNfxbK',
          endpoint: [import.meta.env.MODE === 'production' ? `https://${visitorHost}` : 'https://visitor.toobit.com'],
          scriptUrlPattern: [
            import.meta.env.MODE === 'production'
              ? `https://${visitorHost}/web/v<version>/<apiKey>/loader_v<loaderVersion>.js`
              : 'https://visitor.toobit.com/web/v<version>/<apiKey>/loader_v<loaderVersion>.js'
          ]
        }
      } as FpjsVueOptions)
      .use(VueAnimateOnScroll)
      .use(VueViewPort)
      .use(confirm);
    useGlobalAttrs(app);
    if (isClient) {
      pinia.state.value = (initialState.pinia) || {};
      // 去除测试环境的sentry节约用量（测试时放开）
      // if (import.meta.env.MODE === 'production') {
      Sentry.init({
        app,
        dsn: import.meta.env.MODE === 'production'
          ? 'https://356c05ad7cbcf378f1358cdccbc18740@o4508380357459968.ingest.us.sentry.io/4508522455826432'
          : 'https://5dcce3c6c81c26e29cbcbef6f7a20dc7@o4508380357459968.ingest.us.sentry.io/4508532182941697',
        integrations: [
          Sentry.browserTracingIntegration({ router }),
          Sentry.breadcrumbsIntegration({
            console: false
          }),
          Sentry.extraErrorDataIntegration(),
          Sentry.vueIntegration({
            tracingOptions: {
              trackComponents: true
            }
          })
          // Sentry.replayIntegration({
          //   maskAllText: true,
          //   blockAllMedia: true
          // })
        ],
        maxBreadcrumbs: 10,
        allowUrls: [/https?:\/\/((www)\.)?wcsbapp\.[a-z0-1]{1,}/, /https?:\/\/((www)\.)?toobit\.[a-z0-1]{1,}/],
        // Session Replay
        // replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        // replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        tracesSampleRate: (() => {
          const uid = sessionStorage.getItem('UID');
          // 监控kol报错问题
          if (uid === '598613875') {
            return 1;
          }
          return import.meta.env.MODE === 'production' ? 0.1 : 1;
        })(),
        // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
        // tracePropagationTargets: import.meta.env.MODE === 'production'
        //   ? [/^https:\/\/bapi.toobit.com\/(api|bapi)/]
        //   : [/^https:\/\/bapi.test1.wcsbsapp.com\/(api|bapi)/]
        ignoreErrors: [
          'ResizeObserver loop limit exceeded',
          /NotSupportedError/i,
          'Unable to preload CSS for',
          'Failed to fetch dynamically imported module',
          'error loading dynamically imported module',
          'Non-Error promise rejection captured',
          'node_modules',
          'There was an error when loading the library. Usually this error means the library failed to load its static files. Check that the library files are available at',
          'Failed to load',
          'Object captured as promise rejection with keys',
          'Database deleted by request of the user'
        ],
        initialScope: {
          tags: {
            // 使用 tags 来标记关键维度，方便在 Sentry 上进行筛选和分组
            timeZone: getTimeZone()
          }
        },
        _experiments: { enableLogs: true },

        tracesSampler: () => {
          // 让所有事务都通过 tracesSampler 的初步采样检查
          // 最终是否发送 "Kline API Request" 将由 beforeSendTransaction 决定
          // 对于其他类型的事务，它们将根据全局 tracesSampleRate 被采样
          return true;
        },

        beforeSendTransaction: event => {
          // 检查是 "Kline API Request" 事务，限制上报的span
          if (event.transaction && event.transaction.startsWith('Kline API Request:')) {
            const desiredHttpOps = ['http.dns', 'http.connect', 'http.server'];

            // 1. 清理子span，只保留我们期望的HTTP操作
            if (event.spans && Array.isArray(event.spans)) {
              event.spans = event.spans.filter(span => {
                return span.op && desiredHttpOps.includes(span.op);
              });
            }

            // 2. 根据根span的属性决定是否发送此事务
            // Sentry v8+: 根span (我们的 parentSpan) 的属性理论上在 event.contexts.trace.data
            // 我们在 getKlines 中给 parentSpan 设置了 'kline.slow_response' 和 'error'
            const rootSpanAttributes = event.contexts?.trace?.data || {};
            // 有时，自定义属性可能不会自动提升到 contexts.trace.data，
            // 更可靠的方式可能是检查tags或者直接检查根span对象（如果Sentry内部API允许）
            // 然而，一个更简单的方法是检查整个事务事件中是否有我们设置的标记。
            // Sentry会将根span的属性（attributes）作为交易的标签（tags）或上下文（contexts）的一部分。
            // 我们来检查event.tags中是否有这些标记，因为tags更容易访问。
            // 为了让它工作，我们需要在 getKlines 中使用 parentSpan.setTag() 而不是 parentSpan.setAttribute()
            // 对于 'kline.slow_response' 和 'error'。
            // 暂时，我们先假设它们在 attributes/contexts.trace.data 里。

            const isSlow = rootSpanAttributes['kline.slow_response'] === true;
            // 'error' 属性可能由Sentry在捕获到未处理异常时自动设置，
            // 或者我们手动设置的。
            // 'non_200_error_code' 是我们自定义的。
            const hasJsError = rootSpanAttributes['error'] === true;
            const hasApiErrorCode = typeof rootSpanAttributes['non_200_error_code'] === 'number';

            if (isSlow || hasJsError || hasApiErrorCode) {
              // 这是慢请求或错误请求（JS错误或API非200响应），我们希望发送它
              return event; // 发送
            } else {
              // 这是正常的、快速的请求，我们不希望发送它
              return null; // 丢弃
            }
          }

          // 对于所有其他类型的事务，正常处理 (让它们通过，由tracesSampleRate决定是否采样)
          return event;
        },

        beforeSend(event: any) {
          // 添加 UID tag（如果存在）
          const uid = sessionStorage.getItem('UID');
          if (uid) {
            event.tags = event.tags || {};
            event.tags.uid = uid;
          }

          // unknown 检查错误类型过滤
          const exception = event.exception?.values?.[0];
          if (exception.type === 'Error' && exception.value === 'undefined') {
            return null;
          }

          // 生产环境才需要对域名进行过滤
          if (import.meta.env.MODE === 'production') {
            // 如果事件包含异常信息，检查堆栈跟踪中的帧是否符合允许的 URL
            if (exception?.stacktrace?.frames) {
              for (const frame of exception.stacktrace.frames) {
              // 如果任何帧不符合允许的 URL，则丢弃该事件
                if (!/^https?:\/\/((www\.)?toobit\.[a-z]{2,})/.test(frame.filename)) {
                  return null;
                }
                if (frame.filename.includes('node_modules')) {
                  return null;
                }
              }
            }

            // 检查是否有 HTTP 请求错误，并确保其 URL 符合允许的模式
            if (event.request && !/^https?:\/\/((www\.)?toobit\.[a-z]{2,})/.test(event.request.url)) {
              return null; // 不发送不符合条件的 HTTP 请求错误
            }
          }

          return event;
        }
      });
      // }
    } else {
      app.provide(ID_INJECTION_KEY, {
        prefix: 100,
        current: 0
      });

      onSSRAppRendered(() => {
        initialState.pinia = pinia.state.value;
      });
    }
    // Object.values(import.meta.glob<{ install: UserModule }>('./modules/*.ts', { eager: true }))
    //   .forEach(i => i.install?.(context));

    // 加载路由钩子事件
    useRouterEach(router);
  }
);
